// ./src/contexts/EmNotificationsContext/EmNotificationsContext.tsx
//
// Thin context wrapper around `useEmNotifications` (ROADMAP A2b #57).
//
// ⚠⚠ MOUNTED APP-WIDE BECAUSE THE BELL IS IN THE SHELL — and that is the same slot the DELETED
// carexpenses bell occupied, which is why the shape of this file matters. That one mounted a
// provider app-wide with `autoLoad` and polled `userNotificationList { carId … car { id label } }`
// — a query no eventsmagic subgraph has ever defined — **every 60 seconds for every signed-in
// user**. Nothing failed loudly, because a query the gateway REJECTS looks exactly like an empty
// inbox. `npm run check-nav` asserts that shape stays gone.
//
// ⚠⚠ SO: THIS PROVIDER LOADS THE BADGE COUNT AND **NOTHING ELSE**, AND IT DOES NOT POLL. One cheap
// indexed count on mount; the LIST is fetched only when somebody opens the bell or the page. Adding
// a poll here would recreate the exact cost profile of the thing that was removed, for every
// signed-in session in the product.
import React, { createContext, useContext } from "react";

import { useEmNotifications, UseEmNotificationsOptions, UseEmNotificationsReturn } from "@/hooks/useEmNotifications";

const EmNotificationsContext = createContext<UseEmNotificationsReturn | null>(null);

export interface EmNotificationsProviderProps extends UseEmNotificationsOptions {
  children: React.ReactNode;
}

export const EmNotificationsProvider: React.FC<EmNotificationsProviderProps> = ({ children, ...options }) => {
  const value = useEmNotifications(options);

  return <EmNotificationsContext.Provider value={value}>{children}</EmNotificationsContext.Provider>;
};

export const useEmNotificationsContext = (): UseEmNotificationsReturn => {
  const context = useContext(EmNotificationsContext);

  if (!context) {
    throw new Error(
      "useEmNotificationsContext must be used within an <EmNotificationsProvider>. " +
        "Wrap your component tree with <EmNotificationsProvider> to fix this."
    );
  }

  return context;
};

/**
 * Null-tolerant read.
 *
 * ⚠ THE BELL USES THIS ONE. A crew session signed in by event password has no account user and no
 * bell, and the shell must render perfectly well without a provider rather than throwing on a
 * surface that is otherwise working.
 */
export const useOptionalEmNotificationsContext = (): UseEmNotificationsReturn | null => {
  return useContext(EmNotificationsContext);
};
